import { getBusinessesState } from './businesses.selectors'
import ListTrait from 'src/app/traits/list.trait'
import {
  SetDefaultData,
  UpdateField,
  FetchBusinessesRequest,
} from './businesses.actions'
import { Router, ActivatedRoute } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core'
import { Subscription, Observable, of } from 'rxjs'
import { fromJS } from 'immutable'
import { Map } from 'immutable'
import { FetchLicenseTypesRequest } from '@shared/shared.actions'
import { getLicenseTypesState } from '@shared/shared.select'
import { filtersToDbQuery } from '@shared/shared.utils'
import { TableFilterType } from '@shared/shared.types'

@Component({
  selector: 'app-businesses',
  templateUrl: './businesses.component.html',
  styleUrls: ['./businesses.component.css'],
})
export class BusinessesComponent
  extends ListTrait
  implements OnInit, OnDestroy
{
  public licenseTypes = []
  public licenseTypeModalOpen = false
  public selectedLicenseType: any
  private state$: Subscription
  private route$: Subscription
  private shouldReload: boolean
  public isLoading: boolean
  public licenseTypes$: Subscription
  public columns: object[] = [
    {
      prop: 'code',
      sortProp: 'code',
      name: 'CODE_BUSINESS',
      sortable: true,
      template: 'code',
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'licenseType',
      name: 'LICENSE_TYPE',
      sortable: true,
      sortProp: 'licenseType.name',
      render: (column: Map<string, any>) => {
        return column?.get('name')
      },
    },
    {
      prop: 'propertyConnection',
      name: 'PROPERTY_CODE',
      sortable: true,
      sortProp: 'propertyConnection.property.propertyCodeAssigned',
      template: 'propertyCode',
      filterKey: 'propertyConnection.property.propertyCodeAssigned',
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'subcategoryConnection',
      name: 'CATEGORY',
      sortable: true,
      sortProp: 'subcategoryConnection.subCategory.category.name',
      render: (column: Map<string, any>) => {
        return column?.getIn(['subCategory', 'categoryName'])
      },
    },
    {
      prop: 'subcategoryConnection',
      name: 'SUBCATEGORY',
      sortable: true,
      sortProp: 'subcategoryConnection.subCategory.name',
      maxWidth: '200px',
      render: (column: Map<string, any>) => {
        return column?.getIn(['subCategory', 'name'])
      },
    },
    {
      prop: 'name',
      name: 'HOLDER',
      sortable: true,
      sortProp: 'name',
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'checks',
      name: '',
      template: 'checks',
    },
    {
      prop: 'tin',
      name: 'TIN',
      filterable: true,
      filterType: TableFilterType.String,
      hidden: true,
    },
    {
      prop: 'fromExternal',
      name: 'FROM_EXTERNAL',
      filterable: true,
      filterType: TableFilterType.Boolean,
      hidden: true,
      select$: of(
        fromJS([
          { name: 'true', title: 'Yes', translationKey: 'YES' },
          { name: 'false', title: 'No', translationKey: 'NO' },
        ]),
      ),
    },
    {
      prop: 'checks.hasIssues',
      name: 'HAS_ISSUES',
      filterKey: 'checks.hasIssues',
      filterable: true,
      filterType: TableFilterType.Boolean,
      hidden: true,
      select$: of(
        fromJS([
          { name: 'true', title: 'Yes', translationKey: 'YES' },
          { name: 'false', title: 'No', translationKey: 'NO' },
        ]),
      ),
    },
    {
      prop: 'checks.validationRequired',
      name: 'VALIDATION_REQUIRED',
      filterKey: 'checks.validationRequired',
      filterable: true,
      filterType: TableFilterType.Boolean,
      hidden: true,
      select$: of(
        fromJS([
          { name: 'true', title: 'Yes', translationKey: 'YES' },
          { name: 'false', title: 'No', translationKey: 'NO' },
        ]),
      ),
    },
  ]

  public filters: any[]

  constructor(
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    super(router)
  }

  ngOnInit() {
    this.store.dispatch<any>(new SetDefaultData('list'))
    this.store.dispatch<any>(new FetchLicenseTypesRequest())
    this.licenseTypes$ = this.store
      .pipe(select(getLicenseTypesState))
      .subscribe((state) => {
        this.licenseTypes = state.toJS()
      })
    this.state$ = this.store
      .pipe(select(getBusinessesState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
        this.filters = state.get('filters').toJS()

        if (
          state.get('shouldReload') !== this.shouldReload &&
          state.get('shouldReload')
        ) {
          this.handleFilterChange()
        }
        this.shouldReload = state.get('shouldReload')
      })

    this.route$ = this.route.params.subscribe((params) => {
      // Get status from route path if not in params
      const urlSegments = this.router.url.split('/')
      const status = params.status || urlSegments[2] // /licenses/active or /licenses/archived
      this.handleFilterChange({ ...params, status })
    })
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.route$.unsubscribe()
    this.licenseTypes$.unsubscribe()
  }

  handleFilterChange(data?: any) {
    const filters = data?.filters ?? this.filters
    const pagination = data?.pagination ?? this.state.get('pagination').toJS()
    const { page, page_size, sort, order_by } = pagination
    const status = data?.status

    this.store.dispatch(new UpdateField('filters', filters, 'list'))
    this.store.dispatch(new UpdateField('pagination', pagination, 'list'))

    this.store.dispatch<any>(
      new FetchBusinessesRequest({
        status,
        filters: {
          ...filtersToDbQuery(filters),
        },
        pageSize: parseInt(page_size, 10),
        page: parseInt(page, 10) - 1,
        sort: {
          order: sort?.toUpperCase(),
          prop: order_by,
        },
      }),
    )
  }

  public onClose() {
    this.licenseTypeModalOpen = false
  }

  public getChecksTooltip(row: Map<string, any>): string {
    const messages: string[] = []
    if (row.getIn(['checks', 'hasIssues'])) {
      messages.push('Has issues')
    }
    if (row.getIn(['checks', 'validationRequired'])) {
      messages.push('Validation required')
    }
    return messages.join('<br>')
  }

  public onCreate() {
    this.store.dispatch(
      new UpdateField(
        'data',
        { licenseType: this.selectedLicenseType },
        'create',
      ),
    )
    this.licenseTypeModalOpen = false
    this.selectedLicenseType = null

    this.router.navigate(['/licenses/create'])
  }
}
